import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getProfile, createProfile, updateProfile } from '../../services/communityKitchenService';
import './CommunityKitchen.css';

const STEPS = [
  { number: 1, label: 'Roles' },
  { number: 2, label: 'Location' },
  { number: 3, label: 'Preferences' },
  { number: 4, label: 'Details' },
  { number: 5, label: 'Review' }
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MEALS = ['breakfast', 'lunch', 'dinner'];
const LOGISTICS = ['deliver', 'pickup', 'meetup', 'flexible'];
const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut-Free',
  'Halal', 'Kosher', 'Low-Sodium', 'Diabetic-Friendly'
];

export default function CommunityKitchenOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [formData, setFormData] = useState({
    roles: [],
    displayName: '',
    bio: '',
    zip: '',
    contactInfo: {
      phone: '',
      email: '',
      preferredMethod: 'email'
    },
    tosAccepted: false,
    preferences: {
      radiusMiles: 10,
      logisticsPreference: [],
      dietaryRestrictions: [],
      verificationWilling: false,
      verificationRequired: false,
      availableDays: [],
      availableMeals: []
    },
    recipientDetails: {
      onBehalfOf: false,
      householdSize: 1,
      notes: ''
    },
    cookDetails: {
      maxMealsPerWeek: 3,
      cuisineSpecialties: [],
      hasTransportation: false,
      groceryOrderPreference: 'flexible'
    },
    donorDetails: {
      budgetPerMeal: 2000,
      maxDonationsPerMonth: 10,
      preferDirectInstacart: false,
      groceryOrderPreference: 'flexible'
    }
  });

  // Load existing profile if editing
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const existing = await getProfile();
        if (existing) {
          setIsEditing(true);
          setFormData({
            roles: existing.roles || [],
            displayName: existing.displayName || '',
            bio: existing.bio || '',
            zip: existing.location?.zip || '',
            contactInfo: {
              phone: existing.contactInfo?.phone || '',
              email: existing.contactInfo?.email || '',
              preferredMethod: existing.contactInfo?.preferredMethod || 'email'
            },
            tosAccepted: !!existing.tosAcceptedAt,
            preferences: {
              radiusMiles: existing.preferences?.radiusMiles || 10,
              logisticsPreference: existing.preferences?.logisticsPreference || [],
              dietaryRestrictions: existing.preferences?.dietaryRestrictions || [],
              verificationWilling: existing.preferences?.verificationWilling || false,
              verificationRequired: existing.preferences?.verificationRequired || false,
              availableDays: existing.preferences?.availableDays || [],
              availableMeals: existing.preferences?.availableMeals || []
            },
            recipientDetails: {
              onBehalfOf: existing.recipientDetails?.onBehalfOf || false,
              householdSize: existing.recipientDetails?.householdSize || 1,
              notes: existing.recipientDetails?.notes || ''
            },
            cookDetails: {
              maxMealsPerWeek: existing.cookDetails?.maxMealsPerWeek || 3,
              cuisineSpecialties: existing.cookDetails?.cuisineSpecialties || [],
              hasTransportation: existing.cookDetails?.hasTransportation || false,
              groceryOrderPreference: existing.cookDetails?.groceryOrderPreference || 'flexible'
            },
            donorDetails: {
              budgetPerMeal: existing.donorDetails?.budgetPerMeal || 2000,
              maxDonationsPerMonth: existing.donorDetails?.maxDonationsPerMonth || 10,
              preferDirectInstacart: existing.donorDetails?.preferDirectInstacart || false,
              groceryOrderPreference: existing.donorDetails?.groceryOrderPreference || 'flexible'
            }
          });
        }
      } catch (err) {
        // No profile yet — that's fine, creating new
      } finally {
        setLoadingProfile(false);
      }
    };
    loadExisting();
  }, []);

  const updateField = useCallback((path, value) => {
    setFormData(prev => {
      const parts = path.split('.');
      if (parts.length === 1) {
        return { ...prev, [path]: value };
      }
      const [section, field] = parts;
      return {
        ...prev,
        [section]: { ...prev[section], [field]: value }
      };
    });
  }, []);

  const toggleArrayItem = useCallback((path, item) => {
    setFormData(prev => {
      const parts = path.split('.');
      let arr;
      if (parts.length === 1) {
        arr = prev[path];
      } else {
        arr = prev[parts[0]][parts[1]];
      }
      const newArr = arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];
      if (parts.length === 1) {
        return { ...prev, [path]: newArr };
      }
      return {
        ...prev,
        [parts[0]]: { ...prev[parts[0]], [parts[1]]: newArr }
      };
    });
  }, []);

  const isDonorOnly = formData.roles.length === 1 && formData.roles[0] === 'donor';
  const hasCookOrRecipient = formData.roles.includes('cook') || formData.roles.includes('recipient');

  const canGoNext = () => {
    switch (currentStep) {
      case 1: return formData.roles.length > 0 && formData.displayName.trim().length >= 2;
      case 2: return /^\d{5}$/.test(formData.zip.trim());
      case 3: return isDonorOnly || formData.preferences.logisticsPreference.length > 0;
      case 4: return true;
      case 5: return formData.tosAccepted;
      default: return false;
    }
  };

  const goNext = () => {
    if (canGoNext() && currentStep < STEPS.length) {
      let next = currentStep + 1;
      // Skip step 4 for donor-only (details already in step 3)
      if (next === 4 && isDonorOnly) next = 5;
      setCurrentStep(next);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      let prev = currentStep - 1;
      // Skip step 4 going back for donor-only
      if (prev === 4 && isDonorOnly) prev = 3;
      setCurrentStep(prev);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        tosAcceptedAt: formData.tosAccepted ? new Date().toISOString() : undefined
      };
      delete submitData.tosAccepted;
      if (isEditing) {
        await updateProfile(submitData);
        toast.success('Profile updated!');
      } else {
        await createProfile(submitData);
        toast.success('Welcome to Community Kitchen!');
      }
      navigate('/community-kitchen');
    } catch (err) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step Renderers ───

  const renderStep1 = () => (
    <div className="ck-step">
      <h2>How would you like to participate?</h2>
      <p className="ck-step-desc">Select one or more roles. You can always change these later.</p>

      <div className="ck-role-cards">
        <button
          type="button"
          className={`ck-role-card ${formData.roles.includes('recipient') ? 'selected' : ''}`}
          onClick={() => toggleArrayItem('roles', 'recipient')}
        >
          <span className="ck-role-icon">🍽️</span>
          <strong>Recipient</strong>
          <span className="ck-role-desc">I need help getting meals for myself or someone I know</span>
        </button>
        <button
          type="button"
          className={`ck-role-card ${formData.roles.includes('cook') ? 'selected' : ''}`}
          onClick={() => toggleArrayItem('roles', 'cook')}
        >
          <span className="ck-role-icon">👨‍🍳</span>
          <strong>Cook</strong>
          <span className="ck-role-desc">I want to volunteer my time to shop for and cook meals</span>
        </button>
        <button
          type="button"
          className={`ck-role-card ${formData.roles.includes('donor') ? 'selected' : ''}`}
          onClick={() => toggleArrayItem('roles', 'donor')}
        >
          <span className="ck-role-icon">💝</span>
          <strong>Donor</strong>
          <span className="ck-role-desc">I want to fund meals for people in my community</span>
        </button>
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Display Name *</label>
        <input
          type="text"
          className="ck-input"
          placeholder="First name or nickname (shown to matches)"
          value={formData.displayName}
          onChange={e => updateField('displayName', e.target.value)}
          maxLength={50}
        />
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Bio (optional)</label>
        <textarea
          className="ck-textarea"
          placeholder="Tell others a bit about yourself..."
          value={formData.bio}
          onChange={e => updateField('bio', e.target.value)}
          maxLength={500}
          rows={3}
        />
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Contact Email (shared after funding)</label>
        <input
          type="email"
          className="ck-input"
          placeholder="your@email.com"
          value={formData.contactInfo.email}
          onChange={e => updateField('contactInfo.email', e.target.value)}
        />
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Phone Number (optional, shared after funding)</label>
        <input
          type="tel"
          className="ck-input"
          placeholder="(555) 123-4567"
          value={formData.contactInfo.phone}
          onChange={e => updateField('contactInfo.phone', e.target.value)}
        />
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Preferred Contact Method</label>
        <select
          className="ck-select"
          value={formData.contactInfo.preferredMethod}
          onChange={e => updateField('contactInfo.preferredMethod', e.target.value)}
        >
          <option value="email">Email</option>
          <option value="phone">Phone</option>
          <option value="either">Either</option>
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="ck-step">
      <h2>Where are you located?</h2>
      <p className="ck-step-desc">We use your zip code to find matches nearby. Your exact address is never shown.</p>

      <div className="ck-field-group">
        <label className="ck-label">Zip Code *</label>
        <input
          type="text"
          className="ck-input ck-input-short"
          placeholder="e.g. 30301"
          value={formData.zip}
          onChange={e => updateField('zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
          maxLength={5}
          inputMode="numeric"
        />
      </div>

      <div className="ck-field-group">
        <label className="ck-label">Search Radius: {formData.preferences.radiusMiles} miles</label>
        <input
          type="range"
          className="ck-range"
          min={1}
          max={50}
          step={1}
          value={formData.preferences.radiusMiles}
          onChange={e => updateField('preferences.radiusMiles', parseInt(e.target.value))}
        />
        <div className="ck-range-labels">
          <span>1 mi</span>
          <span>25 mi</span>
          <span>50 mi</span>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="ck-step">
      <h2>{isDonorOnly ? 'Sponsorship Preferences' : 'Your Preferences'}</h2>
      <p className="ck-step-desc">
        {isDonorOnly
          ? 'Tell us about how you\'d like to sponsor meals in your community.'
          : 'Help us find the best matches for you.'}
      </p>

      {/* Donor-only preferences */}
      {isDonorOnly && (
        <>
          <div className="ck-field-group">
            <label className="ck-label">Budget per meal: ${(formData.donorDetails.budgetPerMeal / 100).toFixed(2)}</label>
            <p className="ck-hint">How much are you comfortable sponsoring for a single meal's groceries?</p>
            <input
              type="range"
              className="ck-range"
              min={500}
              max={10000}
              step={500}
              value={formData.donorDetails.budgetPerMeal}
              onChange={e => updateField('donorDetails.budgetPerMeal', parseInt(e.target.value))}
            />
            <div className="ck-range-labels">
              <span>$5</span>
              <span>$50</span>
              <span>$100</span>
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Max sponsorships per month</label>
            <p className="ck-hint">How many meals would you like to fund each month?</p>
            <input
              type="number"
              className="ck-input ck-input-short"
              min={1}
              max={100}
              value={formData.donorDetails.maxDonationsPerMonth}
              onChange={e => updateField('donorDetails.maxDonationsPerMonth', parseInt(e.target.value) || 1)}
            />
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Dietary Preferences to Support</label>
            <p className="ck-hint">Optionally, prioritize matches that serve these diets.</p>
            <div className="ck-chip-grid">
              {DIETARY_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`ck-chip ${formData.preferences.dietaryRestrictions.includes(opt) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferences.dietaryRestrictions', opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Grocery Ordering Preference</label>
            <p className="ck-hint">Who should order groceries from Instacart?</p>
            <select
              className="ck-select"
              value={formData.donorDetails.groceryOrderPreference}
              onChange={e => updateField('donorDetails.groceryOrderPreference', e.target.value)}
            >
              <option value="donor_orders">I'll order groceries and have them delivered</option>
              <option value="cook_orders">Let the cook handle ordering</option>
              <option value="flexible">Flexible — either works</option>
            </select>
          </div>

          <div className="ck-field-group">
            <label className="ck-checkbox-label">
              <input
                type="checkbox"
                checked={formData.preferences.verificationWilling}
                onChange={e => updateField('preferences.verificationWilling', e.target.checked)}
              />
              I'd like to see photos/updates of sponsored meals
            </label>
          </div>
        </>
      )}

      {/* Cook / Recipient preferences */}
      {hasCookOrRecipient && (
        <>
          <div className="ck-field-group">
            <label className="ck-label">Logistics Preference *</label>
            <p className="ck-hint">How would you like meals to be exchanged?</p>
            <div className="ck-chip-grid">
              {LOGISTICS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`ck-chip ${formData.preferences.logisticsPreference.includes(opt) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferences.logisticsPreference', opt)}
                >
                  {opt === 'deliver' ? '🚗 Deliver' : opt === 'pickup' ? '📦 Pickup' : opt === 'meetup' ? '🤝 Meetup' : '🔄 Flexible'}
                </button>
              ))}
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Dietary Restrictions</label>
            <div className="ck-chip-grid">
              {DIETARY_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`ck-chip ${formData.preferences.dietaryRestrictions.includes(opt) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferences.dietaryRestrictions', opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Available Days</label>
            <div className="ck-chip-grid">
              {DAYS.map(day => (
                <button
                  key={day}
                  type="button"
                  className={`ck-chip ${formData.preferences.availableDays.includes(day) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferences.availableDays', day)}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                </button>
              ))}
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-label">Available Meals</label>
            <div className="ck-chip-grid">
              {MEALS.map(meal => (
                <button
                  key={meal}
                  type="button"
                  className={`ck-chip ${formData.preferences.availableMeals.includes(meal) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferences.availableMeals', meal)}
                >
                  {meal.charAt(0).toUpperCase() + meal.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="ck-field-group">
            <label className="ck-checkbox-label">
              <input
                type="checkbox"
                checked={formData.preferences.verificationWilling}
                onChange={e => updateField('preferences.verificationWilling', e.target.checked)}
              />
              I'm willing to share photo/video documentation of meals
            </label>
          </div>

          {formData.roles.includes('recipient') && (
            <div className="ck-field-group">
              <label className="ck-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.preferences.verificationRequired}
                  onChange={e => updateField('preferences.verificationRequired', e.target.checked)}
                />
                I'd like cooks to share documentation with me
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="ck-step">
      <h2>Role Details</h2>
      <p className="ck-step-desc">Tell us more about how you'd like to participate.</p>

      {formData.roles.includes('recipient') && (
        <div className="ck-role-section">
          <h3>🍽️ Recipient Details</h3>
          <div className="ck-field-group">
            <label className="ck-checkbox-label">
              <input
                type="checkbox"
                checked={formData.recipientDetails.onBehalfOf}
                onChange={e => updateField('recipientDetails.onBehalfOf', e.target.checked)}
              />
              I'm signing up on behalf of someone else
            </label>
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Household Size</label>
            <input
              type="number"
              className="ck-input ck-input-short"
              min={1}
              max={20}
              value={formData.recipientDetails.householdSize}
              onChange={e => updateField('recipientDetails.householdSize', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Any notes for your cook?</label>
            <textarea
              className="ck-textarea"
              placeholder="Allergies, preferences, delivery instructions..."
              value={formData.recipientDetails.notes}
              onChange={e => updateField('recipientDetails.notes', e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>
        </div>
      )}

      {formData.roles.includes('cook') && (
        <div className="ck-role-section">
          <h3>👨‍🍳 Cook Details</h3>
          <div className="ck-field-group">
            <label className="ck-label">Max meals per week</label>
            <input
              type="number"
              className="ck-input ck-input-short"
              min={1}
              max={50}
              value={formData.cookDetails.maxMealsPerWeek}
              onChange={e => updateField('cookDetails.maxMealsPerWeek', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Cuisine Specialties</label>
            <input
              type="text"
              className="ck-input"
              placeholder="e.g. Italian, Mexican, Soul Food (comma-separated)"
              value={formData.cookDetails.cuisineSpecialties.join(', ')}
              onChange={e => updateField('cookDetails.cuisineSpecialties', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            />
          </div>
          <div className="ck-field-group">
            <label className="ck-checkbox-label">
              <input
                type="checkbox"
                checked={formData.cookDetails.hasTransportation}
                onChange={e => updateField('cookDetails.hasTransportation', e.target.checked)}
              />
              I have transportation for delivery
            </label>
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Grocery Ordering Preference</label>
            <p className="ck-hint">Who should order groceries from Instacart for your matches?</p>
            <select
              className="ck-select"
              value={formData.cookDetails.groceryOrderPreference}
              onChange={e => updateField('cookDetails.groceryOrderPreference', e.target.value)}
            >
              <option value="cook_orders">I'll order groceries myself</option>
              <option value="donor_orders">Let the donor handle it</option>
              <option value="flexible">Flexible — either works</option>
            </select>
          </div>
        </div>
      )}

      {formData.roles.includes('donor') && !isDonorOnly && (
        <div className="ck-role-section">
          <h3>💝 Donor Details</h3>
          <div className="ck-field-group">
            <label className="ck-label">Budget per meal: ${(formData.donorDetails.budgetPerMeal / 100).toFixed(2)}</label>
            <input
              type="range"
              className="ck-range"
              min={500}
              max={10000}
              step={500}
              value={formData.donorDetails.budgetPerMeal}
              onChange={e => updateField('donorDetails.budgetPerMeal', parseInt(e.target.value))}
            />
            <div className="ck-range-labels">
              <span>$5</span>
              <span>$50</span>
              <span>$100</span>
            </div>
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Max sponsorships per month</label>
            <input
              type="number"
              className="ck-input ck-input-short"
              min={1}
              max={100}
              value={formData.donorDetails.maxDonationsPerMonth}
              onChange={e => updateField('donorDetails.maxDonationsPerMonth', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="ck-field-group">
            <label className="ck-label">Grocery Ordering Preference</label>
            <p className="ck-hint">Who should order groceries from Instacart?</p>
            <select
              className="ck-select"
              value={formData.donorDetails.groceryOrderPreference}
              onChange={e => updateField('donorDetails.groceryOrderPreference', e.target.value)}
            >
              <option value="donor_orders">I'll order groceries and have them delivered</option>
              <option value="cook_orders">Let the cook handle ordering</option>
              <option value="flexible">Flexible — either works</option>
            </select>
          </div>
        </div>
      )}

      {formData.roles.length === 0 && (
        <p className="ck-empty-state">Please go back to Step 1 and select at least one role.</p>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="ck-step">
      <h2>Review Your Profile</h2>
      <p className="ck-step-desc">Make sure everything looks good before joining.</p>

      <div className="ck-review">
        <div className="ck-review-section">
          <h4>Roles</h4>
          <div className="ck-chip-grid">
            {formData.roles.map(role => (
              <span key={role} className="ck-chip selected">
                {role === 'recipient' ? '🍽️' : role === 'cook' ? '👨‍🍳' : '💝'} {role.charAt(0).toUpperCase() + role.slice(1)}
              </span>
            ))}
          </div>
        </div>

        <div className="ck-review-section">
          <h4>About You</h4>
          <p><strong>Name:</strong> {formData.displayName}</p>
          {formData.bio && <p><strong>Bio:</strong> {formData.bio}</p>}
        </div>

        <div className="ck-review-section">
          <h4>Location</h4>
          <p><strong>Zip:</strong> {formData.zip}</p>
          <p><strong>Radius:</strong> {formData.preferences.radiusMiles} miles</p>
        </div>

        <div className="ck-review-section">
          <h4>{isDonorOnly ? 'Sponsorship' : 'Preferences'}</h4>
          {isDonorOnly ? (
            <>
              <p><strong>Budget per meal:</strong> ${(formData.donorDetails.budgetPerMeal / 100).toFixed(2)}</p>
              <p><strong>Max per month:</strong> {formData.donorDetails.maxDonationsPerMonth}</p>
            </>
          ) : (
            <>
              <p><strong>Logistics:</strong> {formData.preferences.logisticsPreference.join(', ') || 'None selected'}</p>
              {formData.preferences.dietaryRestrictions.length > 0 && (
                <p><strong>Dietary:</strong> {formData.preferences.dietaryRestrictions.join(', ')}</p>
              )}
              {formData.preferences.availableDays.length > 0 && (
                <p><strong>Days:</strong> {formData.preferences.availableDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}</p>
              )}
              {formData.preferences.availableMeals.length > 0 && (
                <p><strong>Meals:</strong> {formData.preferences.availableMeals.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}</p>
              )}
            </>
          )}
        </div>

        {formData.contactInfo.email && (
          <div className="ck-review-section">
            <h4>Contact Info</h4>
            <p><strong>Email:</strong> {formData.contactInfo.email}</p>
            {formData.contactInfo.phone && <p><strong>Phone:</strong> {formData.contactInfo.phone}</p>}
            <p><strong>Preferred:</strong> {formData.contactInfo.preferredMethod}</p>
            <p className="ck-hint">Contact info is only shared with match participants after funding.</p>
          </div>
        )}

        <div className="ck-review-section ck-tos-section">
          <h4>Terms &amp; Food Safety Waiver</h4>
          <div className="ck-tos-notice">
            <p>Community Kitchen connects community members to share home-cooked meals. By participating, you acknowledge:</p>
            <ul>
              <li>Meals are prepared in <strong>home kitchens</strong>, not commercial facilities, and may not be subject to health department inspection.</li>
              <li>FreshFitLab is a <strong>platform only</strong> — we facilitate connections but do not prepare, inspect, or guarantee any food.</li>
              <li>You assume responsibility for communicating <strong>allergies and dietary needs</strong> to your match.</li>
              <li>Donors fund grocery purchases; funds are used exclusively for ingredients via Instacart.</li>
            </ul>
          </div>
          <label className="ck-checkbox-label">
            <input
              type="checkbox"
              checked={formData.tosAccepted}
              onChange={e => updateField('tosAccepted', e.target.checked)}
            />
            I have read and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and the food safety waiver above.
          </label>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  if (loadingProfile) {
    return <div className="ck-onboarding"><div className="ck-loading">Loading...</div></div>;
  }

  return (
    <div className="ck-onboarding">
      <div className="ck-onboarding-header">
        <h1>{isEditing ? 'Edit Your Profile' : 'Join Community Kitchen'}</h1>
        <p>{isEditing ? 'Update your details below.' : 'Connect with your community to share meals and make a difference.'}</p>
      </div>

      <div className="ck-step-indicator">
        {STEPS
          .filter(step => !(step.number === 4 && isDonorOnly))
          .map(step => (
          <div
            key={step.number}
            className={`ck-step-dot ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
            onClick={() => step.number < currentStep && setCurrentStep(step.number)}
          >
            <span className="ck-step-number">
              {currentStep > step.number ? '✓' : step.number}
            </span>
            <span className="ck-step-label">{step.label}</span>
          </div>
        ))}
      </div>

      <div className="ck-step-content">
        {renderStepContent()}
      </div>

      <div className="ck-step-actions">
        {currentStep > 1 && (
          <button type="button" className="ck-btn ck-btn-secondary" onClick={goBack}>
            ← Back
          </button>
        )}
        <div className="ck-step-actions-spacer" />
        {currentStep < STEPS.length ? (
          <button
            type="button"
            className="ck-btn ck-btn-primary"
            onClick={goNext}
            disabled={!canGoNext()}
          >
            Next →
          </button>
        ) : (
          <button
            type="button"
            className="ck-btn ck-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : isEditing ? 'Update Profile' : 'Join Community Kitchen'}
          </button>
        )}
      </div>
    </div>
  );
}
